/**
 * AI Art Director — scoring.
 *
 * Every score starts from a clean sheet and is reduced by the findings that
 * touch it, weighted by severity. Deterministic: the same findings always give
 * the same scores.
 */

import { SCORE_CATEGORIES, SEVERITY_WEIGHT } from './reviewSchema';

const PERFECT = 100;
const clampScore = (n) => Math.max(0, Math.min(PERFECT, Math.round(n)));
const round2 = (n) => Math.round(n * 100) / 100;
const clamp01 = (n) => Math.max(0, Math.min(1, n));

/**
 * @param {Array} findings  [{ rule, severity, scores }]
 * @returns {Object} one 0–100 score per category.
 */
export const computeScores = (findings = []) => {
  const scores = {};
  SCORE_CATEGORIES.forEach((category) => {
    const penalty = findings
      .filter((f) => f.scores.includes(category))
      .reduce((sum, f) => sum + (SEVERITY_WEIGHT[f.severity] || 0), 0);
    scores[category] = clampScore(PERFECT - penalty);
  });
  return scores;
};

/**
 * How sure the Art Director is of this review: what the scores say, tempered by
 * how confident the plan and the research behind it were.
 *
 * @param {Object} scores
 * @param {Object} plan
 * @param {Object} research
 * @returns {number} 0–1.
 */
export const computeOverallConfidence = (scores, plan = {}, research = {}) => {
  const values = SCORE_CATEGORIES.map((c) => scores[c] || 0);
  const mean = values.reduce((a, b) => a + b, 0) / values.length / PERFECT;
  const planConfidence = typeof plan.confidence === 'number' ? plan.confidence : 0.5;
  const researchConfidence = typeof research.confidence === 'number' ? research.confidence : 0.4;
  return round2(clamp01(0.6 * mean + 0.25 * planConfidence + 0.15 * researchConfidence));
};
